import { getContentType, calculateHashes, getFileCategory } from './fileUtils';
import { getWaveformData } from './audioPlayerUtils';
import { removeProjectPathFromFilePath } from './filePathUtils';
import type { FileMetadataInput } from '../../generated/graphql';
import { FileCategory } from '../models/enums';
import type { DetectedPlugin } from '../../services/dawPluginDetection';
import { detectPlugins } from '../../services/dawPluginDetection';
import { DAW_EXTENSIONS } from '../../types/daw';
import type { TransferJobLifecycleCallbacks } from '@/context/TransferManagerContext';

/**
 * File metadata for version creation
 * Matches the GraphQL FileMetadataInput type
 */
export type FileMetadata = FileMetadataInput;

export const UPLOAD_ABORT_ERROR_NAME = 'UploadAbortError';

export const createUploadAbortError = (message = 'Upload cancelled') => {
  const error = new Error(message);
  error.name = UPLOAD_ABORT_ERROR_NAME;
  return error;
};

export const isUploadAbortError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === UPLOAD_ABORT_ERROR_NAME || error.name === 'AbortError');

export const throwIfUploadAborted = (abortSignal?: AbortSignal) => {
  if (abortSignal?.aborted) {
    throw createUploadAbortError();
  }
};

/**
 * Prepare file metadata array for version creation
 * @param files - Array of File objects to prepare metadata for
 * @param projectFolderPath - The project folder path to remove from file paths
 * @returns Promise resolving to an array of file metadata objects
 */
export async function prepareFileMetadataForUpload(
  files: File[],
  projectFolderPath: string
): Promise<FileMetadata[]> {
  const newFiles: FileMetadata[] = [];

  for (const file of files) {
    const contentType = getContentType(file);
    const relativePath =
      (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
      file.name;

    // Remove the project folder path from the file path
    const filePathInProject = removeProjectPathFromFilePath(
      relativePath,
      projectFolderPath,
      file.name
    );

    // Calculate hashes
    const { quickHash, fullHash } = await calculateHashes(file);

    // Calculate waveForm data when the file is audio
    const waveForm =
      getFileCategory(contentType, file.name) === FileCategory.AUDIO
        ? await getWaveformData(file)
        : null;

    newFiles.push({
      fileName: filePathInProject,
      contentType,
      size: file.size,
      quickHash,
      fullHash,
      waveForm: waveForm || null,
    } as unknown as FileMetadataInput);
  }

  return newFiles;
}

/**
 * Upload a single file to S3 using a presigned URL
 * @param file - The File object to upload
 * @param presignedUrl - The presigned URL for uploading
 * @param onProgress - Optional callback for upload progress (0-100)
 * @param fullHash - Manifest SHA-256 hex, required for signed checksum headers
 * @returns Promise resolving to true if upload succeeds
 * @throws Error if the upload request fails
 */
export async function uploadFileToS3(
  file: File,
  presignedUrl: string,
  onProgress?: (progress: number) => void,
  abortSignal?: AbortSignal,
  fullHash?: string,
  contentType?: string
): Promise<boolean> {
  throwIfUploadAborted(abortSignal);

  const headers: Record<string, string> = {
    'Content-Type': contentType ?? getContentType(file),
  };
  const signedHeaders = new URL(presignedUrl).searchParams
    .get('X-Amz-SignedHeaders')
    ?.toLowerCase()
    .split(';');
  if (signedHeaders?.includes('x-amz-checksum-sha256')) {
    // Reuse the manifest digest. S3 must reject bytes changed since hashing.
    if (
      !fullHash ||
      fullHash.length !== 64 ||
      !/^[a-f0-9]{64}$/i.test(fullHash)
    ) {
      throw new Error('Missing or invalid SHA-256 for verified upload');
    }
    headers['x-amz-checksum-sha256'] = btoa(
      String.fromCharCode(
        ...fullHash.match(/.{2}/g)!.map((byte) => parseInt(byte, 16))
      )
    );
  }

  const canUseXHRProgress =
    !!onProgress &&
    typeof XMLHttpRequest !== 'undefined' &&
    (typeof navigator === 'undefined' || !/jsdom/i.test(navigator.userAgent));

  if (canUseXHRProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', presignedUrl);
      for (const [name, value] of Object.entries(headers)) {
        xhr.setRequestHeader(name, value);
      }

      let settled = false;
      const settle = (
        callback: (value?: boolean | Error) => void,
        value?: boolean | Error
      ) => {
        if (settled) return;
        settled = true;
        if (abortSignal && abortHandler) {
          abortSignal.removeEventListener('abort', abortHandler);
        }
        callback(value);
      };

      const abortHandler = () => {
        xhr.abort();
      };
      if (abortSignal) {
        abortSignal.addEventListener('abort', abortHandler);
      }

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || !onProgress) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          settle(() => resolve(true));
          return;
        }

        settle(
          (error) => reject(error as Error),
          new Error(
            `Upload failed for ${file.name}: ${xhr.statusText || `HTTP ${xhr.status}`}`
          )
        );
      };

      xhr.onerror = () => {
        settle(
          (error) => reject(error as Error),
          new Error(`Failed to upload ${file.name}: Network error`)
        );
      };

      xhr.onabort = () => {
        settle((error) => reject(error as Error), createUploadAbortError());
      };

      xhr.send(file);
    });
  }

  try {
    const fetchOptions: RequestInit = {
      method: 'PUT',
      body: file,
      headers,
    };
    if (abortSignal) {
      fetchOptions.signal = abortSignal;
    }

    const response = await fetch(presignedUrl, {
      ...fetchOptions,
    });

    if (!response.ok) {
      const errorMessage = `Upload failed for ${file.name}: ${response.statusText}`;
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    if (onProgress) {
      onProgress(100);
    }

    return true;
  } catch (error) {
    if (isUploadAbortError(error)) {
      throw createUploadAbortError();
    }

    // Re-throw the error for fail-fast behavior
    const errorMessage =
      error instanceof Error
        ? error.message
        : `Failed to upload ${file.name}: ${String(error)}`;
    console.error(`Error uploading ${file.name}:`, error);
    throw new Error(errorMessage);
  }
}

/**
 * Upload multiple files to S3 with progress tracking
 * @param files - Array of File objects to upload
 * @param uploadUrls - Array of presigned URLs corresponding to files
 * @param onProgress - Optional callback for overall progress (0-100) and current file name
 * @returns Promise resolving to the number of successfully uploaded files
 * @throws Error if any file fails to upload (fails fast on first failure)
 * @deprecated Use `pushVersionAndUpload` instead — it matches files by fullHash rather than
 * positional index and handles the version push + cleanup callback in one call.
 */
export async function uploadFilesToS3(
  files: File[],
  uploadUrls: Array<{ presignedUrl?: string; uploadRequired?: boolean }>,
  onProgress?: (progress: number, currentFile?: string) => void
): Promise<number> {
  let uploadedCount = 0;
  const totalFiles = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const uploadUrl = uploadUrls[i];

    if (!uploadUrl?.presignedUrl) {
      if (uploadUrl?.uploadRequired === false) {
        // File already exists in S3 (deduped) — count as uploaded and skip
        uploadedCount++;
        continue;
      }
      throw new Error(`No upload URL provided for ${file.name}`);
    }

    // uploadFileToS3 will throw on failure, stopping the loop immediately
    await uploadFileToS3(file, uploadUrl.presignedUrl, (fileProgress) => {
      // Calculate overall progress: base progress + (file progress * file weight)
      const baseProgress = (uploadedCount / totalFiles) * 100;
      const fileWeight = 100 / totalFiles;
      const overallProgress = baseProgress + (fileProgress * fileWeight) / 100;
      if (onProgress) {
        onProgress(overallProgress, file.name);
      }
    });

    uploadedCount++;
  }

  return uploadedCount;
}

/**
 * Detect DAW plugins from an array of files.
 * Checks each file's extension against known DAW extensions, reads the file
 * buffer, and runs plugin detection. Errors per file are swallowed so that
 * detection failures do not abort the upload/sync flow.
 * @param files - Array of File objects to scan
 * @returns Promise resolving to a flat array of detected plugins
 */
export async function detectPluginsFromFiles(
  files: File[]
): Promise<DetectedPlugin[]> {
  const allDetectedPlugins: DetectedPlugin[] = [];
  for (const file of files) {
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext in DAW_EXTENSIONS) {
      try {
        const buf = await file.arrayBuffer();
        const plugins = await detectPlugins(buf, file.name);
        allDetectedPlugins.push(...plugins);
      } catch (e) {
        console.warn('[uploadUtils] Plugin detection failed for', file.name, e);
      }
    }
  }
  return allDetectedPlugins;
}

export interface VersionPushCommitData {
  commitMessage: string;
  newFiles: FileMetadata[];
  updatedFiles: FileMetadata[];
  deletedFileIds: string[];
  plugins: DetectedPlugin[];
}

export interface VersionPushOptions {
  onContext?: TransferJobLifecycleCallbacks['onContext'];
  onDiagnostic?: TransferJobLifecycleCallbacks['onDiagnostic'];
  projectId: string;
  commitData: VersionPushCommitData;
  /** Pre-computed hashes — no double hash in caller */
  filesToUpload: Array<{ file: File; fullHash: string; fileName?: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pushMutation: (variables: any) => Promise<any>;
  finalizeMutation: (options: {
    variables: { projectId: string; versionId: string };
  }) => Promise<{ data?: { projectVersionFinalize?: boolean } | null }>;
  /** Called with the new versionId when an S3 upload fails so callers can rollback or delete */
  onUploadError: (versionId: string) => Promise<void>;
  onProgress?: (pct: number) => void;
  onStatusMessage?: (message: string) => void;
  onFileStart?: (fileName: string) => void;
  onFileProgress?: (fileName: string, progress: number) => void;
  onFileComplete?: (fileName: string) => void;
  onFileError?: (fileName: string, error: string) => void;
  abortSignal?: AbortSignal;
}

/**
 * Push a new project version via GraphQL and upload files to S3.
 * Uses hash-based URL matching (not positional index).
 * On S3 failure, calls `onUploadError(versionId)` then re-throws.
 * @returns The new version ID
 */
export async function pushVersionAndUpload(
  options: VersionPushOptions
): Promise<{ versionId: string }> {
  const MIN_FILE_SETTLE_MS = 250;
  const settleAtHundredPercent = () =>
    new Promise((resolve) => setTimeout(resolve, MIN_FILE_SETTLE_MS));

  const {
    projectId,
    commitData,
    filesToUpload,
    pushMutation,
    onUploadError,
    onProgress,
    onStatusMessage,
    onFileStart,
    onFileProgress,
    onFileComplete,
    onFileError,
    abortSignal,
  } = options;

  throwIfUploadAborted(abortSignal);

  options.onContext?.({
    projectId,
    files: [...commitData.newFiles, ...commitData.updatedFiles].map((file) => ({
      fileName: file.fileName,
      size: file.size,
      fullHash: file.fullHash,
      quickHash: file.quickHash,
      replacesFileId: file.replacesFileId ?? undefined,
    })),
    deletedFileIds: commitData.deletedFileIds,
  });
  const versionResult = await pushMutation({
    variables: { projectId, data: { ...commitData, uploadProtocol: 2 } },
  });

  if (!versionResult.data?.projectVersionPush) {
    throw new Error('Failed to create project version');
  }

  const { uploadUrls, version } = versionResult.data.projectVersionPush;
  const versionId: string = version._id;
  options.onContext?.({ projectId, versionId });
  let cleanupAttempted = false;
  const cleanupOnUploadFailure = async () => {
    if (cleanupAttempted) return;
    cleanupAttempted = true;
    await onUploadError(versionId);
  };

  // Equal bytes can have multiple required destinations. Consume every entry
  // once, rather than overwriting an earlier upload with a dedup response.
  const urlMap = new Map<
    string,
    { presignedUrl?: string; uploadRequired?: boolean }[]
  >();
  try {
    if (!Array.isArray(uploadUrls))
      throw new Error('Missing upload instructions');
    for (const entry of uploadUrls) {
      if (!entry || typeof entry.fullHash !== 'string')
        throw new Error('Invalid upload instructions');
      const entries = urlMap.get(entry.fullHash) ?? [];
      entries.push(entry);
      urlMap.set(entry.fullHash, entries);
    }
    // Validate the complete response before any file is reported complete.
    const available = new Map(
      [...urlMap].map(([hash, entries]) => [hash, [...entries]])
    );
    for (const file of filesToUpload) {
      const entry = available.get(file.fullHash)?.shift();
      if (!entry || (entry.uploadRequired !== false && !entry.presignedUrl)) {
        throw new Error(
          `Missing required upload instructions for ${file.fileName || file.file.name}`
        );
      }
    }
    if ([...available.values()].some((entries) => entries.length))
      throw new Error('Unexpected upload instructions');
  } catch (error) {
    await cleanupOnUploadFailure();
    throw error;
  }

  const totalFiles = filesToUpload.length;
  const fileProgressByName = new Map<string, number>();
  onStatusMessage?.('Uploading');

  const reportOverallProgress = () => {
    if (!onProgress) return;
    if (totalFiles === 0) {
      onProgress(100);
      return;
    }

    let totalProgress = 0;
    fileProgressByName.forEach((progress) => {
      totalProgress += progress;
    });
    onProgress(Math.round(totalProgress / totalFiles));
  };

  const uploadSingleFile = async (
    file: File,
    fullHash: string,
    fileName?: string
  ) => {
    throwIfUploadAborted(abortSignal);

    const transferFileName = fileName || file.name;
    const urlEntry = urlMap.get(fullHash)!.shift()!;

    fileProgressByName.set(transferFileName, 0);
    onFileStart?.(transferFileName);

    // Only an explicit deduplication response can skip a PUT.
    if (urlEntry.uploadRequired === false) {
      fileProgressByName.set(transferFileName, 100);
      onFileProgress?.(transferFileName, 100);
      reportOverallProgress();
      await settleAtHundredPercent();
      onFileComplete?.(transferFileName);
      return;
    }

    await uploadFileToS3(
      file,
      urlEntry.presignedUrl!,
      (progress) => {
        fileProgressByName.set(transferFileName, progress);
        onFileProgress?.(transferFileName, progress);
        reportOverallProgress();
      },
      abortSignal,
      fullHash
    );

    fileProgressByName.set(transferFileName, 100);
    onFileProgress?.(transferFileName, 100);
    reportOverallProgress();
    await settleAtHundredPercent();
    onFileComplete?.(transferFileName);
  };

  try {
    const pending = [...filesToUpload];
    const active = new Set<Promise<void>>();
    const MAX_CONCURRENT = 10;
    let firstError: Error | null = null;

    while (pending.length > 0 || active.size > 0) {
      // Drain in-flight writes before rolling metadata back. Otherwise a retry
      // can overlap still-running PUTs from the cancelled version.
      if (abortSignal?.aborted) pending.length = 0;

      while (
        active.size < MAX_CONCURRENT &&
        pending.length > 0 &&
        !abortSignal?.aborted
      ) {
        const { file, fullHash, fileName } = pending.shift()!;
        const transferFileName = fileName || file.name;

        const uploadPromise = uploadSingleFile(file, fullHash, fileName)
          .catch((error) => {
            const uploadError =
              error instanceof Error ? error : new Error('Upload failed');
            if (!firstError) {
              firstError = uploadError;
            }
            if (!isUploadAbortError(uploadError)) {
              const metadata = [
                ...commitData.newFiles,
                ...commitData.updatedFiles,
              ].find((item) => item.fileName === transferFileName);
              options.onDiagnostic?.('Upload file', error, {
                fileName: transferFileName,
                fullHash,
                size: file.size,
                quickHash: metadata?.quickHash,
              });
              onFileError?.(transferFileName, uploadError.message);
            }
          })
          .finally(() => {
            active.delete(uploadPromise);
          });

        active.add(uploadPromise);
      }

      if (active.size > 0) {
        await Promise.race(active);
      }

      if (abortSignal?.aborted) {
        pending.length = 0;
      }
    }

    if (firstError) {
      await cleanupOnUploadFailure();
      throw firstError;
    }

    throwIfUploadAborted(abortSignal);

    reportOverallProgress();
  } catch (error) {
    if (isUploadAbortError(error)) {
      await cleanupOnUploadFailure();
      throw createUploadAbortError();
    }

    if (error instanceof Error) {
      throw error;
    }
    throw error;
  }

  // Never run destructive cleanup after requesting publication: a network error
  // can mean the server committed successfully but its response was lost.
  onStatusMessage?.('Verifying cloud files');
  try {
    const finalized = await options.finalizeMutation({
      variables: { projectId, versionId },
    });
    if (finalized.data?.projectVersionFinalize !== true) {
      throw new Error('Server did not confirm version publication');
    }
  } catch (error) {
    const publicationError = new Error(
      'Could not confirm the cloud version. Refresh the project before retrying; your local files have not been changed.'
    );
    (publicationError as Error & { cause?: unknown }).cause = error;
    options.onDiagnostic?.('Verify and publish version', publicationError);
    throw publicationError;
  }
  return { versionId };
}
